import { MODEL_LABEL, modelLabel, parseModel } from './format';
import type { Lock, LockModel, LockStatus } from './types';

/**
 * Slotvoorraad op schaal (10.000+ sloten): zoeken, filteren, sorteren en pagineren gebeurt server-side
 * (RPC `inventory_search`, migratie 0013). De demo gebruikt `searchInventoryRows` met exact dezelfde regels.
 */

export type InventorySort = 'serial' | 'model' | 'status' | 'club' | 'door' | 'firmware' | 'battery' | 'reset_card';
export type SortDir = 'asc' | 'desc';

export const INVENTORY_SORTS: readonly InventorySort[] = ['serial', 'model', 'status', 'club', 'door', 'firmware', 'battery', 'reset_card'];
export const INVENTORY_PAGE_SIZE = 50;
/** Bovengrens voor één export (zelfde als de RPC). */
export const INVENTORY_EXPORT_LIMIT = 20000;

export interface InventoryQuery {
  search?: string;
  status?: LockStatus | '';
  model?: LockModel | '';
  /** '' = alle, 'none' = nog bij Antreja, anders organisatie-id. */
  org?: string;
  sort: InventorySort;
  dir: SortDir;
  limit: number;
  offset: number;
}

export interface InventoryRow {
  lock: Lock;
  organization_name: string | null;
  door_id: string | null;
  door_name: string | null;
  location_name: string | null;
}

export interface InventoryResult {
  rows: InventoryRow[];
  total: number;
}

/** Levenscyclus-volgorde voor sorteren op status (zelfde als de RPC). */
export const STATUS_ORDER: Record<LockStatus, number> = { in_stock: 1, assigned: 2, installed: 3, faulty: 4, retired: 5 };

export function isInventorySort(v: string | null | undefined): v is InventorySort {
  return !!v && (INVENTORY_SORTS as readonly string[]).includes(v);
}

function sortKey(r: InventoryRow, sort: InventorySort): string | number | null {
  const low = (s: string | null | undefined) => (s == null ? null : s.toLowerCase());
  switch (sort) {
    case 'serial':
      return low(r.lock.serial);
    case 'model':
      return MODEL_LABEL[r.lock.model];
    case 'status':
      return STATUS_ORDER[r.lock.status];
    case 'club':
      return low(r.organization_name);
    case 'door':
      return low(r.door_name);
    case 'firmware':
      return low(r.lock.firmware);
    case 'battery':
      return r.lock.battery_pct;
    case 'reset_card':
      return low(r.lock.reset_card_held_by);
  }
}

const cmp = (a: string | number, b: string | number) => (a < b ? -1 : a > b ? 1 : 0);

/** Zoeken/filteren/sorteren/pagineren in het geheugen, met dezelfde regels als `inventory_search`. */
export function searchInventoryRows(all: InventoryRow[], q: InventoryQuery): InventoryResult {
  const term = q.search?.trim().toLowerCase() ?? '';
  const has = (s: string | null | undefined) => !!s && s.toLowerCase().includes(term);
  const filtered = all.filter(
    (r) =>
      (!q.status || r.lock.status === q.status) &&
      (!q.model || r.lock.model === q.model) &&
      (!q.org || (q.org === 'none' ? !r.lock.organization_id : r.lock.organization_id === q.org)) &&
      (!term ||
        has(r.lock.serial) ||
        has(r.lock.firmware) ||
        has(r.organization_name) ||
        has(r.door_name) ||
        has(r.location_name) ||
        has(modelLabel(r.lock.model))),
  );
  const dir = q.dir === 'desc' ? -1 : 1;
  filtered.sort((a, b) => {
    const ka = sortKey(a, q.sort);
    const kb = sortKey(b, q.sort);
    if (ka !== kb) {
      if (ka === null) return 1; // nulls last, in beide richtingen
      if (kb === null) return -1;
      const c = cmp(ka, kb) * dir;
      if (c) return c;
    }
    const sa = a.lock.serial?.toLowerCase() ?? null;
    const sb = b.lock.serial?.toLowerCase() ?? null;
    if (sa !== sb) {
      if (sa === null) return 1;
      if (sb === null) return -1;
      return cmp(sa, sb);
    }
    return cmp(a.lock.id, b.lock.id);
  });
  const offset = Math.max(0, q.offset);
  const limit = Math.min(Math.max(1, q.limit), INVENTORY_EXPORT_LIMIT);
  return { rows: filtered.slice(offset, offset + limit), total: filtered.length };
}

/** "1–50 van 1.234" */
export function rangeLabel(offset: number, count: number, total: number): string {
  const nl = (n: number) => n.toLocaleString('nl-NL');
  if (!total || !count) return `0 van ${nl(total)}`;
  return `${nl(offset + 1)}–${nl(offset + count)} van ${nl(total)}`;
}

// ---------------------------------------------------------------------------------------------
// Levering invoeren
// ---------------------------------------------------------------------------------------------

export interface DeliveryItem {
  serial: string;
  /** Model van de regel zelf (A1V/A1K/A1T, of de oude namen), anders null = standaardmodel. */
  model: LockModel | null;
}

const HEADER_WORDS = new Set(['model', 'type', 'serienummer', 'serienummers', 'serial', 'sn', 'number', 'nummer']);

/**
 * Leest geplakte serienummers uit een pakbon. Per regel mag een model staan (`A1K;SN123`, `SN123 A1V`);
 * de oude leveranciersnamen (D1, D1-R, D1-K) worden stil herkend. Kopregels worden overgeslagen.
 */
export function parseDelivery(text: string): DeliveryItem[] {
  const items: DeliveryItem[] = [];
  for (const line of text.split(/\r?\n/)) {
    const tokens = line
      .split(/[\s,;\t]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    let lineModel: LockModel | null = null;
    const serials: string[] = [];
    for (const t of tokens) {
      const m = parseModel(t);
      if (m) lineModel = m;
      else if (!HEADER_WORDS.has(t.toLowerCase())) serials.push(t);
    }
    for (const serial of serials) items.push({ serial, model: lineModel });
  }
  return items;
}

/** Groepeert per model; regels zonder model krijgen `fallback`. Null als er een regel zonder model overblijft. */
export function groupDelivery(items: DeliveryItem[], fallback: LockModel | ''): Map<LockModel, string[]> | null {
  const out = new Map<LockModel, string[]>();
  for (const it of items) {
    const m = it.model ?? (fallback || null);
    if (!m) return null;
    out.set(m, [...(out.get(m) ?? []), it.serial]);
  }
  return out;
}
